<!--
Компонент ProductFilters - фильтры для товаров по себестоимости и предложениям магазина

Примеры использования:

<ProductFilters 
  :store_id="activeStoreId" 
  :store_name="activeStoreName"
  :order_type="activeOrderType"
  @filtersChanged="handleFiltersChanged" 
/>

Пропсы:
- store_id - ID активного магазина (String/Number)
- store_name - название активного магазина (String)
- order_type - тип заказа (fbs, fbo и т.д.) (String)

Компонент предоставляет фильтры:
- cost_min, cost_max - фильтры по себестоимости
- offer_store_id - ID магазина для фильтрации предложений (автоматически устанавливается из store_id)
- order_type - тип заказа для фильтрации (автоматически устанавливается из пропа)
- offer_price_min, offer_price_max - фильтры по цене в магазине (если магазин выбран)
- offer_profit_min, offer_profit_max - фильтры по прибыли в сумме (если магазин выбран)
- offer_profit_percent_min, offer_profit_percent_max - фильтры по прибыли в процентах от цены (если магазин выбран)
- offer_profit_percent_cost_min, offer_profit_percent_cost_max - фильтры по прибыли в процентах от себестоимости (если магазин выбран)
-->

<template>
  <div class="position-relative">
    <button 
      class="btn btn-outline-secondary waves-effect w-100" 
      @click="toggleFilters"
    >
      <i class="ti ti-filter me-1"></i>
      Фильтры
      <span v-if="activeFiltersCount > 0" class="badge bg-primary ms-1">{{ activeFiltersCount }}</span>
    </button>
    
    <div v-if="showFilters" class="position-absolute top-100 start-0 mt-2" style="z-index: 1000;">
      <div class="card shadow border-0" style="min-width: 400px;">
        <div class="pt-3 card-body">
          <!-- Группа: Основные фильтры -->
          <div class="mb-4">
            <div class="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2" 
                 style="cursor: pointer;" 
                 @click="toggleGroup('basic')">
              <h6 class="text-muted mb-0">
                <i class="ti ti-filter me-1"></i>
                Основные фильтры
              </h6>
              <i :class="expandedGroups.basic ? 'ti ti-chevron-up' : 'ti ti-chevron-down'" class="text-muted"></i>
            </div>
            <div v-show="expandedGroups.basic" class="ps-0">
              <div class="mb-3">
                <label class="form-label fw-medium mb-2">Себестоимость</label>
                <div class="d-flex gap-2">
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="От"
                    v-model="filters.cost_min"
                  >
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="До"
                    v-model="filters.cost_max"
                  >
                </div>
              </div>
              
              <div class="mb-3">
                <label class="form-label fw-medium mb-2">Бренды</label>
                <input 
                  type="text" 
                  class="form-control" 
                  placeholder="Введите бренды через запятую"
                  v-model="filters.brands"
                >
              </div>
            </div>
          </div>
          
          <!-- Группа: Фильтры по магазину (если магазин выбран) -->
          <div v-if="store_id" class="mb-4">
            <div class="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2" 
                 style="cursor: pointer;" 
                 @click="toggleGroup('store')">
              <h6 class="text-muted mb-0">
                <i class="ti ti-building-store me-1"></i>Фильтры по магазину
                <span class="badge bg-primary ms-2">{{ store_name }}</span>
                <i class="ti ti-info-circle ti-sm ms-1" 
                   v-tooltip.bottom="'Для переключения магазина выберите другой в настройках отображения таблицы'"
                   style="cursor: help;"></i>
              </h6>
              <i :class="expandedGroups.store ? 'ti ti-chevron-up' : 'ti ti-chevron-down'" class="text-muted"></i>
            </div>
            <div v-show="expandedGroups.store" class="ps-0">
              <div class="mb-3">
                <div class="form-check form-switch">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    v-model="filters.offer_exists" 
                    id="offerExists"
                  >
                  <label class="form-check-label" for="offerExists">
                    Только товары этого магазина
                  </label>
                </div>
              </div>
              
              <div class="mb-3">
                <label class="form-label fw-medium mb-2">Цена в магазине</label>
                <div class="d-flex gap-2">
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="От"
                    v-model="filters.offer_price_min"
                  >
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="До"
                    v-model="filters.offer_price_max"
                  >
                </div>
              </div>
              
              <div class="mb-3">
                <label class="form-label fw-medium mb-2">Прибыль (сумма)</label>
                <div class="d-flex gap-2">
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="От"
                    v-model="filters.offer_profit_min"
                  >
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="До"
                    v-model="filters.offer_profit_max"
                  >
                </div>
              </div>
              
              <div class="mb-3">
                <label class="form-label fw-medium mb-2">Прибыль от цены (%)</label>
                <div class="d-flex gap-2">
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="От"
                    v-model="filters.offer_profit_percent_min"
                  >
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="До"
                    v-model="filters.offer_profit_percent_max"
                  >
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-medium mb-2">Прибыль от себестоимости (%)</label>
                <div class="d-flex gap-2">
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="От"
                    v-model="filters.offer_profit_percent_cost_min"
                  >
                  <input 
                    type="number" 
                    class="form-control" 
                    placeholder="До"
                    v-model="filters.offer_profit_percent_cost_max"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card-footer d-flex gap-2 border-top">
          <button class="btn btn-primary flex-fill" @click="applyFilters">
            Применить
          </button>
          <button class="btn btn-outline-secondary flex-fill" @click="resetFilters">
            Сбросить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from 'primevue/tooltip';

export default {
  name: 'ProductFilters',
  directives: {
    tooltip: Tooltip
  },
  props: {
    // ID активного магазина
    store_id: {
      type: [String, Number],
      default: null
    },
    // Название активного магазина
    store_name: {
      type: String,
      default: null
    },
    // Тип заказа (fbs, fbo и т.д.)
    order_type: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showFilters: false,
      expandedGroups: {
        basic: true,
        store: true
      },
      filters: {
        cost_min: null,
        cost_max: null,
        brands: null,
        offer_store_id: null,
        order_type: null,
        offer_exists: null,
        offer_price_min: null,
        offer_price_max: null,
        offer_profit_min: null,
        offer_profit_max: null,
        offer_profit_percent_min: null,
        offer_profit_percent_max: null,
        offer_profit_percent_cost_min: null,
        offer_profit_percent_cost_max: null
      }
    }
  },
  computed: {
    activeFiltersCount() {
      return Object.entries(this.filters)
        .filter(([key, value]) => {
          // Исключаем служебные параметры из подсчета
          if (key === 'offer_store_id' || key === 'order_type') return false;
          // Для offer_exists учитываем только true значения
          if (key === 'offer_exists') return value === true;
          return value !== null && value !== '';
        })
        .length;
    }
  },
  methods: {
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    
    toggleGroup(groupName) {
      this.expandedGroups[groupName] = !this.expandedGroups[groupName];
    },
    
    applyFilters() {
      // Собираем все фильтры для передачи
      const cleanFilters = {};
      
      Object.entries(this.filters).forEach(([key, value]) => {
        // Исключаем служебные параметры из основного цикла
        if (key === 'offer_store_id' || key === 'order_type') return;
        
        if (key === 'brands' && value) {
          cleanFilters[key] = value.split(',').map(brand => brand.trim()).filter(brand => brand.length > 0);
        } else if (key === 'offer_exists') {
          // Передаем offer_exists только если true
          if (value === true) {
            cleanFilters[key] = true;
          }
        } else if (value !== null && value !== '') {
          cleanFilters[key] = value;
        }
      });
      
      // Передаем служебные параметры только если есть фильтры по магазину
      if (this.hasStoreFilters() || this.filters.offer_exists === true) {
        if (this.filters.offer_store_id !== null) {
          cleanFilters.offer_store_id = this.filters.offer_store_id;
        }
        if (this.filters.order_type !== null) {
          cleanFilters.order_type = this.filters.order_type;
        }
      } else {
        // В противном случае явно убеждаемся, что offer_store_id отсутствует
        if (Object.prototype.hasOwnProperty.call(cleanFilters, 'offer_store_id')) {
          delete cleanFilters.offer_store_id;
        }
      }
      
      this.$emit('filtersChanged', cleanFilters);
      this.toggleFilters();
    },
    
    resetFilters() {
      this.filters = {
        offer_store_id: this.filters.offer_store_id,
        cost_min: null,
        cost_max: null,
        brands: null,
        order_type: null,
        offer_exists: null,
        offer_price_min: null,
        offer_price_max: null,
        offer_profit_min: null,
        offer_profit_max: null,
        offer_profit_percent_min: null,
        offer_profit_percent_max: null,
        offer_profit_percent_cost_min: null,
        offer_profit_percent_cost_max: null
      };
      this.$emit('filtersChanged', {});
    },

    hasStoreFilters() {
      const isSet = (v) => v !== null && v !== '';
      return isSet(this.filters.offer_price_min) ||
             isSet(this.filters.offer_price_max) ||
             isSet(this.filters.offer_profit_min) ||
             isSet(this.filters.offer_profit_max) ||
             isSet(this.filters.offer_profit_percent_min) ||
             isSet(this.filters.offer_profit_percent_max) ||
             isSet(this.filters.offer_profit_percent_cost_min) ||
             isSet(this.filters.offer_profit_percent_cost_max);
    }
  },
  mounted() {
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.showFilters = false;
      }
    });
  },
  watch: {
    // Автоматически устанавливаем offer_store_id при изменении store_id
    store_id: {
      handler(newStoreId) {
        this.filters.offer_store_id = newStoreId;
        // Автоматически применяем фильтры при изменении магазина, если есть фильтры по магазину
        if (this.hasStoreFilters()) {
          this.applyFilters();
        }
      },
      immediate: true
    },
    // Автоматически устанавливаем order_type при изменении пропа
    order_type: {
      handler(newOrderType) {
        this.filters.order_type = newOrderType;
        // Автоматически применяем фильтры при изменении типа заказа, если есть фильтры по магазину
        if (this.hasStoreFilters()) {
          this.applyFilters();
        }
      },
      immediate: true
    }
  }
}
</script>